/**
 * Registro central dos nomes de eventos de métrica.
 *
 * Convenção: `PLANNINGAPP_<SEÇÃO>_<ALVO>_<AÇÃO>` — maiúsculas, `_` entre segmentos,
 * `-` dentro de um segmento, ação em past tense (`_CLICKED`, `_SELECTED`, `_TOGGLED`).
 *
 * A chave da constante é o nome do evento sem o prefixo `PLANNINGAPP_` e com `-` virando `_`.
 * **Os valores são o contrato com o Amplitude**: renomear uma string quebra a série histórica
 * do evento, então mudanças de valor precisam ser combinadas com quem mantém os dashboards.
 *
 * `sendMetrics` (`src/presentation/utils/sendMetrics.ts`) aceita apenas `MetricsEventName`,
 * então um nome fora desta lista não compila. Ao adicionar um evento novo, declare aqui.
 *
 * Inconsistências herdadas, mantidas de propósito para não quebrar histórico (ver PLA-228):
 * - `APP_ACCESS` é o único sem o prefixo `PLANNINGAPP_`.
 * - `MAP_ADHP_PROCEDURES_SELECTED` e `ADHP_INFO_NOTAM_BACKGROUND_REFRESH` têm 4–5 segmentos,
 *   fora do padrão de 4.
 * - `USER_CHARTS_DOWNLOAD_ORIGINAL` não tem sufixo de ação.
 * - `ADHP_INFO_NOTAM_BACKGROUND_REFRESH` usa verbo no presente, não past tense.
 * - Chaves de payload são majoritariamente `camelCase`, mas o `NotamPanel` usa `snake_case`
 *   (`refresh_reason`, `has_cache`).
 */
export const MetricsEvents = {
  // ---------------------------------------------------------------------------
  // App / global
  // ---------------------------------------------------------------------------

  /** Acesso ao app. Emitido pelo `TrackingService` ao liberar o envio de métricas. */
  APP_ACCESS: 'APP_ACCESS',

  // ---------------------------------------------------------------------------
  // Navegação
  // ---------------------------------------------------------------------------

  /** Entrada em uma tela. Payload: `{ screen, path, previousScreen, mode? }`. */
  PAGE_ENTERED: 'PLANNINGAPP_PAGE_ENTERED',
  /**
   * Troca de aba em grupos de aba que NÃO mudam a rota. `group` identifica o conjunto e
   * `tab` a aba escolhida. Grupos cuja aba é rota real (ex. Meus Conteúdos) não usam este
   * evento — já são medidos por `PAGE_ENTERED`.
   */
  TAB_SELECTED: 'PLANNINGAPP_TAB_SELECTED',

  // ---------------------------------------------------------------------------
  // Toolbar do mapa
  // ---------------------------------------------------------------------------

  /** Clique em um botão da toolbar que abre painel. Payload: `{ extra: <tool>, active }`. */
  TOOLBAR_TOOL_CLICKED: 'PLANNINGAPP_TOOLBAR_TOOL_CLICKED',
  /** Régua ativada. */
  TOOLBAR_RULER_CLICKED: 'PLANNINGAPP_TOOLBAR_RULER_CLICKED',
  /** Zoom/ajuste de enquadramento. Payload: `{ extra: 'fit' | 'zoom-in' | 'zoom-out' }`. */
  TOOLBAR_ZOOM_CLICKED: 'PLANNINGAPP_TOOLBAR_ZOOM_CLICKED',
  /** Aeródromo/helipouso alternado. Payload: `{ extra, active }`. */
  TOOLBAR_ADHP_SELECTED: 'PLANNINGAPP_TOOLBAR_ADHP_SELECTED',
  /** Legenda de pontos no mapa aberta. */
  TOOLBAR_ADHP_LEGEND_CLICKED: 'PLANNINGAPP_TOOLBAR_ADHP-LEGEND_CLICKED',
  /** Filtro de comprimento mínimo de pista. */
  TOOLBAR_ADHP_MIN_TRACK_LENGTH_CLICKED: 'PLANNINGAPP_TOOLBAR_ADHP-MIN-TRACK-LENGTH_CLICKED',
  /** Filtro de pontos no mapa. Payload: `{ extra, active }`. */
  TOOLBAR_ADHP_FILTER_SELECTED: 'PLANNINGAPP_TOOLBAR_ADHP-FILTER_SELECTED',
  /** Camada de pontos do usuário alternada pela toolbar. */
  TOOLBAR_ADHP_USER_WAYPOINTS_SELECTED: 'PLANNINGAPP_TOOLBAR_ADHP-USER-WAYPOINTS_SELECTED',
  /** Camada de auxílios à navegação alternada. */
  TOOLBAR_NAVAIDS_SELECTED: 'PLANNINGAPP_TOOLBAR_NAVAIDS_SELECTED',
  /** Camada de obstáculos alternada. */
  TOOLBAR_OBSTACLES_SELECTED: 'PLANNINGAPP_TOOLBAR_OBSTACLES_SELECTED',
  /** Tipo de espaço aéreo alternado. Payload: `{ extra: <layerKey>, active }`. */
  TOOLBAR_AIRSPACES_TYPE_SELECTED: 'PLANNINGAPP_TOOLBAR_AIRSPACES-TYPE_SELECTED',
  /** Carta/mapa base alternado. Payload: `{ extra: <chartKey>, active }`. */
  TOOLBAR_CHARTS_MAP_SELECTED: 'PLANNINGAPP_TOOLBAR_CHARTS-MAP_SELECTED',
  /** Tipo de combustível alternado. Payload: `{ extra: 'avgas' | 'jet-a', active }`. */
  TOOLBAR_FUEL_TYPE_SELECTED: 'PLANNINGAPP_TOOLBAR_FUEL-TYPE_SELECTED',
  /** Camada de meteorologia alternada. Payload: `{ extra, active }`. */
  TOOLBAR_METEOROLOGY_TYPE_SELECTED: 'PLANNINGAPP_TOOLBAR_METEOROLOGY-TYPE_SELECTED',

  // ---------------------------------------------------------------------------
  // Drawer — rota, rotas salvas, cartas de procedimento, VNav e outros painéis
  // ---------------------------------------------------------------------------

  /** Rota salva. Payload: `{ extra: 'save-route(yes)' | 'new-route' }`. */
  DRAWER_ROUTE_SAVED: 'PLANNINGAPP_DRAWER_ROUTE_SAVED',
  /** Rota invertida. */
  DRAWER_ROUTE_INVERTED: 'PLANNINGAPP_DRAWER_ROUTE_INVERTED',
  /** Rota segmentada. */
  DRAWER_ROUTE_SEGMENTED: 'PLANNINGAPP_DRAWER_ROUTE_SEGMENTED',
  /** Rota exportada. Payload: `{ type: 'pdf' | 'garmin' | 'MF FS2020' | 'XPlane 10' | 'XPlane 11' }`. */
  DRAWER_ROUTE_EXPORTED: 'PLANNINGAPP_DRAWER_ROUTE_EXPORTED',
  /** Rota limpa. */
  DRAWER_ROUTE_CLEAR_CLICKED: 'PLANNINGAPP_DRAWER_ROUTE-CLEAR_CLICKED',
  /** Rota salva selecionada. */
  DRAWER_SAVED_ROUTE_SELECTED: 'PLANNINGAPP_DRAWER_SAVED-ROUTE_SELECTED',
  /** Rota salva apagada. */
  DRAWER_SAVED_ROUTE_DELETED: 'PLANNINGAPP_DRAWER_SAVED-ROUTE_DELETED',
  /** Ação numa carta de procedimento. Payload: `{ extra: 'download' | 'plot-on-map' }`. */
  DRAWER_CHARTS_ACTION_CLICKED: 'PLANNINGAPP_DRAWER_CHARTS-ACTION_CLICKED',
  /** Tipo de carta de procedimento selecionado. Payload: `{ type }`. */
  DRAWER_CHARTS_TYPE_SELECTED: 'PLANNINGAPP_DRAWER_CHARTS-TYPE_SELECTED',
  /** Altitude de cruzeiro selecionada na Navegação Vertical. Payload: `{ filter, value }`. */
  DRAWER_VNAV_CRUISE_ALTITUDE_SELECTED: 'PLANNINGAPP_DRAWER_VNAV-CRUISE-ALTITUDE_SELECTED',
  /** Market (iframe de serviços) carregado. */
  DRAWER_SERVICES_SELECTED: 'PLANNINGAPP_DRAWER_SERVICES_SELECTED',
  /** Loja do app mobile. Payload: `{ extra: 'play-store' | 'app-store' }`. */
  DRAWER_APP_CLICKED: 'PLANNINGAPP_DRAWER_APP_CLICKED',

  // ---------------------------------------------------------------------------
  // Mapa
  // ---------------------------------------------------------------------------

  /** Clique em um ponto do mapa a partir da busca. */
  MAP_POINT_CLICKED: 'PLANNINGAPP_MAP_POINT_CLICKED',
  /** Aeródromo selecionado no mapa. Payload: `{ extra: 'add-to-route' | 'info' }`. */
  MAP_ADHP_SELECTED: 'PLANNINGAPP_MAP_ADHP_SELECTED',
  /** Cartas de procedimento abertas pelo balão do aeródromo. */
  MAP_ADHP_PROCEDURES_SELECTED: 'PLANNINGAPP_MAP_ADHP_PROCEDURES_SELECTED',
  /** Serviços do aeródromo abertos pelo balão. */
  MAP_ADHP_SERVICES_SELECTED: 'PLANNINGAPP_MAP_ADHP-SERVICES_SELECTED',
  /** Espaço aéreo selecionado no mapa. */
  MAP_AIRSPACES_SELECTED: 'PLANNINGAPP_MAP_AIRSPACES_SELECTED',

  // ---------------------------------------------------------------------------
  // Busca e suporte
  // ---------------------------------------------------------------------------

  /** Foco no campo de busca principal. */
  SEARCH_INPUT_CLICKED: 'PLANNINGAPP_SEARCH_INPUT_CLICKED',
  /** Abertura do chat de suporte. */
  SUPPORT_CHAT_CLICKED: 'PLANNINGAPP_SUPPORT_CHAT_CLICKED',

  // ---------------------------------------------------------------------------
  // Configurações e Novidades
  // ---------------------------------------------------------------------------

  /**
   * Ação na tela de Configurações. `extra` discrimina qual (logout, links externos,
   * navegação para sub-telas) — mesmo formato de `DRAWER_CHARTS_ACTION_CLICKED`.
   */
  SETTINGS_ACTION_CLICKED: 'PLANNINGAPP_SETTINGS_ACTION_CLICKED',
  /** Modo de inserção de waypoints alterado. Payload: `{ type }`. */
  SETTINGS_ROUTE_MODE_CLICKED: 'PLANNINGAPP_SETTINGS_ROUTE-MODE_CLICKED',
  /**
   * Abertura da tela de Novidades, com o estado de "não lido" no momento da abertura —
   * é a única informação que o `PAGE_ENTERED` não consegue capturar, porque o presenter
   * marca o último item como visto ao carregar.
   */
  NEWS_OPENED: 'PLANNINGAPP_NEWS_OPENED',

  // ---------------------------------------------------------------------------
  // Informações de aeródromo (ADHP Info)
  // ---------------------------------------------------------------------------

  /** Atualização de NOTAMs em background. Payload: `{ icao, refresh_reason, has_cache }`. */
  ADHP_INFO_NOTAM_BACKGROUND_REFRESH: 'PLANNINGAPP_ADHP-INFO_NOTAM_BACKGROUND_REFRESH',
  /** NOTAM destacado. Payload: `{ icao, identifier, action }`. */
  ADHP_INFO_NOTAM_HIGHLIGHTED: 'PLANNINGAPP_ADHP-INFO_NOTAM_HIGHLIGHTED',

  // ---------------------------------------------------------------------------
  // Combustível
  // ---------------------------------------------------------------------------

  /** Clique em um ponto de abastecimento no mapa. É o único caminho que abre o InfoPanel de combustível. */
  FUEL_MAP_POINT_CLICKED: 'PLANNINGAPP_FUEL_MAP-POINT_CLICKED',
  /** Abertura do formulário "Informar quanto paguei". */
  FUEL_REPORT_PRICE_CLICKED: 'PLANNINGAPP_FUEL_REPORT-PRICE_CLICKED',
  /** Abertura do formulário "Cadastrar fornecedor". Payload traz `origin`. */
  FUEL_REGISTER_PROVIDER_CLICKED: 'PLANNINGAPP_FUEL_REGISTER-PROVIDER_CLICKED',
  /** Abertura da edição de um fornecedor já cadastrado. */
  FUEL_EDIT_PROVIDER_CLICKED: 'PLANNINGAPP_FUEL_EDIT-PROVIDER_CLICKED',
  /** Preço reportado com sucesso. */
  FUEL_PRICE_SAVED: 'PLANNINGAPP_FUEL_PRICE_SAVED',
  /** Falha ao reportar preço. */
  FUEL_PRICE_FAILED: 'PLANNINGAPP_FUEL_PRICE_FAILED',
  /** Fornecedor cadastrado/editado com sucesso. Payload traz `mode`. */
  FUEL_PROVIDER_SAVED: 'PLANNINGAPP_FUEL_PROVIDER_SAVED',
  /** Falha ao cadastrar/editar fornecedor. Payload traz `mode`. */
  FUEL_PROVIDER_FAILED: 'PLANNINGAPP_FUEL_PROVIDER_FAILED',
  /** Clique no WhatsApp de um fornecedor. */
  FUEL_PROVIDER_WHATSAPP_CLICKED: 'PLANNINGAPP_FUEL_PROVIDER-WHATSAPP_CLICKED',

  // ---------------------------------------------------------------------------
  // Peso e Balanceamento — perfis
  // ---------------------------------------------------------------------------

  /** Perfil de P&B salvo. Payload: `{ mode, complete }`. */
  WB_PROFILE_SAVED: 'PLANNINGAPP_WB-PROFILE_SAVED',
  /** Perfil de P&B apagado. */
  WB_PROFILE_DELETED: 'PLANNINGAPP_WB-PROFILE_DELETED',
  /** Perfil de P&B duplicado pela lista da aeronave. Payload: `{ complete }`, do original (igual ao da cópia). */
  WB_PROFILE_DUPLICATED: 'PLANNINGAPP_WB-PROFILE_DUPLICATED',
  /** Perfil de P&B renomeado pela lista da aeronave; nome igual ao atual não conta. Payload: `{ complete }`. */
  WB_PROFILE_RENAMED: 'PLANNINGAPP_WB-PROFILE_RENAMED',
  /** Modo de criação do perfil. Payload: `{ mode: 'template' | 'another-aircraft' | 'manual' }`. */
  WB_PROFILE_CREATION_MODE_SELECTED: 'PLANNINGAPP_WB-PROFILE_CREATION-MODE_SELECTED',

  // ---------------------------------------------------------------------------
  // Peso e Balanceamento — preenchimentos
  // ---------------------------------------------------------------------------

  /** Preenchimento criado no Flight Setup. */
  WB_FILL_CREATED: 'PLANNINGAPP_WB-FILL_CREATED',
  /** Preenchimento salvo (não emitido em auto-save). */
  WB_FILL_SAVED: 'PLANNINGAPP_WB-FILL_SAVED',
  /** Preenchimento apagado. */
  WB_FILL_DELETED: 'PLANNINGAPP_WB-FILL_DELETED',
  /** Preenchimento duplicado. Payload traz `origin`. */
  WB_FILL_DUPLICATED: 'PLANNINGAPP_WB-FILL_DUPLICATED',
  /** Preenchimento exportado em PDF. Payload: `{ signed, origin }`. */
  WB_FILL_EXPORTED: 'PLANNINGAPP_WB-FILL_EXPORTED',
  /** Falha ao gerar o PDF do preenchimento. */
  WB_FILL_EXPORT_FAILED: 'PLANNINGAPP_WB-FILL_EXPORT_FAILED',
  /** Modo de criação do preenchimento. Espelha `WB_PROFILE_CREATION_MODE_SELECTED`. */
  WB_FILL_CREATION_MODE_SELECTED: 'PLANNINGAPP_WB-FILL_CREATION-MODE_SELECTED',
  /** Abertura do modal de assinatura, que antecede a exportação. */
  WB_FILL_SIGNATURE_CLICKED: 'PLANNINGAPP_WB-FILL_SIGNATURE_CLICKED',
  /** Campo de estação editado. Payload: `{ field }`. */
  WB_FILL_INPUT_EDITED: 'PLANNINGAPP_WB-FILL_INPUT_EDITED',
  /** Unidade de uma aba de estações alterada. Payload: `{ tab, unit }`. */
  WB_FILL_UNIT_CHANGED: 'PLANNINGAPP_WB-FILL_UNIT_CHANGED',
  /** Seletor de peso padrão aberto de dentro de um preenchimento. Payload: `{ tab }`. */
  WB_FILL_DEFAULT_WEIGHT_CLICKED: 'PLANNINGAPP_WB-FILL_DEFAULT-WEIGHT_CLICKED',
  /** Atalho de "tanque completo". */
  WB_FILL_COMPLETE_TANK_CLICKED: 'PLANNINGAPP_WB-FILL_COMPLETE-TANK_CLICKED',
  /** Entrada em "Novo P&B" bloqueada. Payload traz `reason`. */
  WB_FILL_NEW_BLOCKED: 'PLANNINGAPP_WB-FILL_NEW-BLOCKED',
  /** CTA "Cadastrar" do modal de bloqueio, que leva à tela de aeronaves. */
  WB_FILL_REGISTER_AIRCRAFT_CLICKED: 'PLANNINGAPP_WB-FILL_REGISTER-AIRCRAFT_CLICKED',
  /** CTA para cadastrar perfil de P&B da aeronave escolhida no Flight Setup. */
  WB_FILL_REGISTER_PROFILE_CLICKED: 'PLANNINGAPP_WB-FILL_REGISTER-PROFILE_CLICKED',

  // ---------------------------------------------------------------------------
  // Peso e Balanceamento — pesos padrão. `origin` distingue a tela de gerenciamento do
  // cadastro feito de dentro de um preenchimento (`WBStations`).
  // ---------------------------------------------------------------------------

  /** Abertura do formulário de novo peso padrão. */
  WB_DEFAULT_WEIGHT_NEW_CLICKED: 'PLANNINGAPP_WB-DEFAULT-WEIGHT_NEW_CLICKED',
  /** Abertura da edição de um peso padrão existente. */
  WB_DEFAULT_WEIGHT_EDIT_CLICKED: 'PLANNINGAPP_WB-DEFAULT-WEIGHT_EDIT_CLICKED',
  /** Peso padrão criado ou atualizado. Payload traz `mode` e `origin`. */
  WB_DEFAULT_WEIGHT_SAVED: 'PLANNINGAPP_WB-DEFAULT-WEIGHT_SAVED',
  /** Falha ao salvar peso padrão. */
  WB_DEFAULT_WEIGHT_FAILED: 'PLANNINGAPP_WB-DEFAULT-WEIGHT_FAILED',
  /** Peso padrão apagado. */
  WB_DEFAULT_WEIGHT_DELETED: 'PLANNINGAPP_WB-DEFAULT-WEIGHT_DELETED',

  // ---------------------------------------------------------------------------
  // Checklists. Entrada nas telas já é medida por `PAGE_ENTERED` (todas são rotas reais),
  // então não existe evento de "abriu a lista".
  // ---------------------------------------------------------------------------

  /** Botão de recarregar da lista de checklists. */
  CHECKLISTS_LIST_REFRESHED: 'PLANNINGAPP_CHECKLISTS_LIST_REFRESHED',
  /** Botão "Novo checklist". */
  CHECKLISTS_CREATE_CLICKED: 'PLANNINGAPP_CHECKLISTS_CREATE_CLICKED',
  /** Ação de editar um checklist — o clique na linha da lista. */
  CHECKLISTS_EDIT_CLICKED: 'PLANNINGAPP_CHECKLISTS_EDIT_CLICKED',
  /** Duplicação confirmada no modal. */
  CHECKLISTS_DUPLICATE_CLICKED: 'PLANNINGAPP_CHECKLISTS_DUPLICATE_CLICKED',
  /** Exclusão confirmada no modal. */
  CHECKLISTS_DELETE_CLICKED: 'PLANNINGAPP_CHECKLISTS_DELETE_CLICKED',
  /** Origem do conteúdo escolhida no passo 2 do builder.
   * Payload: `{ source: 'same-model' | 'other-model' | 'scratch' }`. */
  CHECKLISTS_TEMPLATE_SELECTED: 'PLANNINGAPP_CHECKLISTS_TEMPLATE_SELECTED',
  /** Checklist salvo com sucesso no passo 3 do builder.
   * Payload: `{ mode: 'create' | 'edit', proceduresCount, checksCount }`. */
  CHECKLISTS_BUILDER_SAVED: 'PLANNINGAPP_CHECKLISTS_BUILDER_SAVED',
  /** Procedimentos reordenados por drag-and-drop. Payload: `{ type: ChecklistProcedureType }`. */
  CHECKLISTS_PROCEDURE_REORDERED: 'PLANNINGAPP_CHECKLISTS_PROCEDURE_REORDERED',
  /** Itens de um procedimento reordenados por drag-and-drop. Payload: `{ itemType: ChecklistItemType }`. */
  CHECKLISTS_ITEM_REORDERED: 'PLANNINGAPP_CHECKLISTS_ITEM_REORDERED',
  /** Ação "Compartilhar" da linha da lista — o download do `.nxc`. */
  CHECKLISTS_EXPORT_CLICKED: 'PLANNINGAPP_CHECKLISTS_EXPORT_CLICKED',
  /**
   * Opção "Importar arquivo .nxc" ou "Importar CSV" do menu de importação, antes de o arquivo ser
   * escolhido. Payload: `{ format: 'nxc' | 'csv' }` (eventos anteriores ao PLA-352 não trazem `format`
   * e são sempre `.nxc`).
   */
  CHECKLISTS_IMPORT_CLICKED: 'PLANNINGAPP_CHECKLISTS_IMPORT_CLICKED',
  /** Importação concluída. Payload: `{ count, format }`. */
  CHECKLISTS_IMPORT_SUCCESS: 'PLANNINGAPP_CHECKLISTS_IMPORT_SUCCESS',
  /**
   * Importação recusada ou falhada. Payload: `{ code, format }` — `code` é um dos
   * `NEXATLAS_COMPONENT_*`/`CHECKLIST_CSV_*`, ou `CHECKLIST_CSV_INVALID_ROWS` com `errorCount` quando
   * o CSV tem linhas erradas.
   */
  CHECKLISTS_IMPORT_FAILED: 'PLANNINGAPP_CHECKLISTS_IMPORT_FAILED',
  /** Opção "Baixar template CSV" do menu de importação: o arquivo foi baixado. */
  CHECKLISTS_CSV_TEMPLATE_DOWNLOADED: 'PLANNINGAPP_CHECKLISTS_CSV_TEMPLATE_DOWNLOADED',
  /** "Copiar instruções" do modal do template: as instruções para a IA foram para a área de transferência. */
  CHECKLISTS_CSV_INSTRUCTIONS_COPIED: 'PLANNINGAPP_CHECKLISTS_CSV_INSTRUCTIONS_COPIED',

  // ---------------------------------------------------------------------------
  // Meus Conteúdos — Cartas do usuário
  // ---------------------------------------------------------------------------

  /** Carta do usuário exibida/ocultada. Payload: `{ chartId, action, origin }`. */
  USER_CHARTS_TOGGLED: 'PLANNINGAPP_USER-CHARTS_TOGGLED',
  /** Carta renomeada. Payload: `{ chartId }`. */
  USER_CHARTS_RENAMED: 'PLANNINGAPP_USER-CHARTS_RENAMED',
  /** Carta apagada. Payload: `{ chartId }`. */
  USER_CHARTS_DELETED: 'PLANNINGAPP_USER-CHARTS_DELETED',
  /** Arquivo da carta reenviado. Payload: `{ chartId }`. */
  USER_CHARTS_REUPLOADED: 'PLANNINGAPP_USER-CHARTS_REUPLOADED',
  /** Download do arquivo original da carta. Payload: `{ chartId }`. */
  USER_CHARTS_DOWNLOAD_ORIGINAL: 'PLANNINGAPP_USER-CHARTS_DOWNLOAD-ORIGINAL',
  /** Início do upload de carta. Payload: `{ extension, sizeMb }`. */
  USER_CHARTS_UPLOAD_STARTED: 'PLANNINGAPP_USER-CHARTS_UPLOAD-STARTED',
  /** Upload de carta concluído. Payload: `{ extension, sizeMb }`. */
  USER_CHARTS_UPLOAD_SUCCESS: 'PLANNINGAPP_USER-CHARTS_UPLOAD-SUCCESS',
  /** Falha no upload de carta. Payload: `{ extension, errorCode }`. */
  USER_CHARTS_UPLOAD_FAILED: 'PLANNINGAPP_USER-CHARTS_UPLOAD-FAILED',

  // ---------------------------------------------------------------------------
  // Meus Conteúdos — KML/KMZ (geometrias do usuário)
  // ---------------------------------------------------------------------------

  /** Geometria exibida/ocultada. Payload: `{ origin, state }`. */
  USER_GEOMETRIES_TOGGLED: 'PLANNINGAPP_USER-GEOMETRIES_TOGGLED',
  /** Geometria importada e salva. Payload: `{ featureCount }`. */
  USER_GEOMETRIES_SAVED: 'PLANNINGAPP_USER-GEOMETRIES_SAVED',
  /** Geometria apagada. */
  USER_GEOMETRIES_DELETED: 'PLANNINGAPP_USER-GEOMETRIES_DELETED',
  /** Início da importação de KML/KMZ. */
  USER_GEOMETRIES_IMPORT_STARTED: 'PLANNINGAPP_USER-GEOMETRIES_IMPORT_STARTED',
  /** Falha na importação de KML/KMZ. Payload: `{ code }`. */
  USER_GEOMETRIES_IMPORT_FAILED: 'PLANNINGAPP_USER-GEOMETRIES_IMPORT_FAILED',
  /** Edição de geometria salva. `nameChanged`/`stylesChanged` dizem o que mudou. */
  USER_GEOMETRIES_EDIT_SAVED: 'PLANNINGAPP_USER-GEOMETRIES_EDIT_SAVED',
  /** Falha ao salvar a edição. Payload traz `step`. */
  USER_GEOMETRIES_EDIT_FAILED: 'PLANNINGAPP_USER-GEOMETRIES_EDIT_FAILED',
  /** Download do arquivo original. Espelha `USER_CHARTS_DOWNLOAD_ORIGINAL`. */
  USER_GEOMETRIES_DOWNLOAD_ORIGINAL: 'PLANNINGAPP_USER-GEOMETRIES_DOWNLOAD-ORIGINAL',
  /** Falha no download do arquivo original. */
  USER_GEOMETRIES_DOWNLOAD_FAILED: 'PLANNINGAPP_USER-GEOMETRIES_DOWNLOAD_FAILED',

  // ---------------------------------------------------------------------------
  // Meus Conteúdos — Pontos do usuário
  // ---------------------------------------------------------------------------

  /** Ponto do usuário criado. */
  USER_WAYPOINTS_CREATED: 'PLANNINGAPP_USER-WAYPOINTS_CREATED',
  /** Falha ao criar ponto do usuário. */
  USER_WAYPOINTS_CREATE_FAILED: 'PLANNINGAPP_USER-WAYPOINTS_CREATE_FAILED',
  /** Ponto do usuário atualizado. */
  USER_WAYPOINTS_UPDATED: 'PLANNINGAPP_USER-WAYPOINTS_UPDATED',
  /** Falha ao atualizar ponto do usuário. */
  USER_WAYPOINTS_UPDATE_FAILED: 'PLANNINGAPP_USER-WAYPOINTS_UPDATE_FAILED',
  /** Ponto(s) apagado(s). Payload traz `batch` e `count`. */
  USER_WAYPOINTS_DELETED: 'PLANNINGAPP_USER-WAYPOINTS_DELETED',
  /** Falha ao apagar ponto(s). Payload traz `batch` e `count`. */
  USER_WAYPOINTS_DELETE_FAILED: 'PLANNINGAPP_USER-WAYPOINTS_DELETE_FAILED',
  /** Visibilidade de ponto(s) alternada. Espelha `USER_GEOMETRIES_TOGGLED`. */
  USER_WAYPOINTS_TOGGLED: 'PLANNINGAPP_USER-WAYPOINTS_TOGGLED',
  /** "Mostrar no mapa" de um ponto. */
  USER_WAYPOINTS_SHOW_ON_MAP_CLICKED: 'PLANNINGAPP_USER-WAYPOINTS_SHOW-ON-MAP_CLICKED',
  /** Pontos exportados em CSV. Payload traz `count`. */
  USER_WAYPOINTS_EXPORTED: 'PLANNINGAPP_USER-WAYPOINTS_EXPORTED',
  /** Falha ao exportar pontos (hoje silenciosa também na UI). */
  USER_WAYPOINTS_EXPORT_FAILED: 'PLANNINGAPP_USER-WAYPOINTS_EXPORT_FAILED',
  /** Início de importação de pontos. Payload traz `extension`. */
  USER_WAYPOINTS_IMPORT_STARTED: 'PLANNINGAPP_USER-WAYPOINTS_IMPORT_STARTED',
  /** Importação concluída. `partial` indica que parte das linhas foi rejeitada. */
  USER_WAYPOINTS_IMPORT_SUCCESS: 'PLANNINGAPP_USER-WAYPOINTS_IMPORT_SUCCESS',
  /** Falha na importação de pontos. */
  USER_WAYPOINTS_IMPORT_FAILED: 'PLANNINGAPP_USER-WAYPOINTS_IMPORT_FAILED'
} as const

export type MetricsEventName = (typeof MetricsEvents)[keyof typeof MetricsEvents]
